import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { addSeconds, format, getUnixTime, startOfDay } from 'date-fns';
import { restartableTask } from 'ember-concurrency';
import type DeskModel from 'garaje/models/desk';
import type MapFloorModel from 'garaje/models/map-floor';
import type WorkplaceDayModel from 'garaje/models/workplace-day';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LoggerService from 'garaje/services/logger';
import { SECONDS_IN_A_DAY } from 'garaje/utils/enums';
import urlBuilder from 'garaje/utils/url-builder';

type Phase = 'desk-selection' | 'move-details';

interface CurrentDeskInfo {
  name: string;
  floorName: string | null;
  neighborhoodName: string | null;
}

interface MoveRequestModalArgs {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  currentDesk: CurrentDeskInfo | null;
  hasCurrentDesk: boolean;
  floors: MapFloorModel[];
  currentFloorId: string | null;
  employeeId: string;
  employeeEmail: string;
  locationId: string;
  timezone: string;
  workplaceDays?: WorkplaceDayModel[];
  selectedDate?: Date;
}

export default class MoveRequestModalComponent extends Component<{ Args: MoveRequestModalArgs }> {
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;
  @service declare logger: LoggerService;
  @service declare store: Store;

  @tracked phase: Phase = 'desk-selection';
  @tracked selectedDesk: DeskModel | null = null;
  @tracked selectedFloorId: string | null = null;
  @tracked moveDate: Date | null = null;
  @tracked moveRequestReason = '';
  @tracked isSubmitting = false;
  @tracked conflictAssignee: string | null = null;
  @tracked conflictNeighborhood: string | null = null;
  @tracked conflictBlocked = false;

  get isDeskSelectionPhase(): boolean {
    return this.phase === 'desk-selection';
  }

  get isMoveDetailsPhase(): boolean {
    return this.phase === 'move-details';
  }

  get canProceedToDetails(): boolean {
    return this.selectedDesk !== null;
  }

  get canSubmit(): boolean {
    return this.moveDate != null && this.moveRequestReason.trim().length > 0;
  }

  get hasConflict(): boolean {
    return this.conflictAssignee != null || this.conflictNeighborhood != null || this.conflictBlocked;
  }

  get selectedDeskDisplayText(): string {
    if (!this.selectedDesk) return '';
    const name = this.selectedDesk.name;
    const floorId = this.selectedDesk.belongsTo('floor').id();
    const floor = floorId ? this.selectedDesk.store.peekRecord('floor', floorId) : null;
    return [name, floor?.name].filter(Boolean).join(', ');
  }

  get selectedDeskNeighborhood(): string | null {
    return this.selectedDesk?.neighborhoodName ?? null;
  }

  get currentDeskDisplayText(): string {
    if (!this.args.currentDesk) return '';
    return [this.args.currentDesk.name, this.args.currentDesk.floorName].filter(Boolean).join(', ');
  }

  get currentDeskNeighborhood(): string | null {
    return this.args.currentDesk?.neighborhoodName ?? null;
  }

  get buttonText(): string {
    return this.args.hasCurrentDesk ? 'Request desk change' : 'Request desk assignment';
  }

  get title(): string {
    return this.args.hasCurrentDesk ? 'Request desk change' : 'Request desk assignment';
  }

  /**
   * Returns the working hours start time (e.g., "08:00:00") for the selected move date,
   * or "00:00:00" if no active workplace day is configured for that day.
   */
  get moveDateStartTime(): string {
    if (!this.moveDate) return '00:00:00';
    const dayOfWeek = format(this.moveDate, 'EEEE');
    const activeDay = this.args.workplaceDays?.find((d) => d.active && d.dayOfWeek === dayOfWeek);
    return activeDay?.startTime ?? '00:00:00';
  }

  get activeFloorId(): string | null {
    return this.selectedFloorId ?? this.args.currentFloorId;
  }

  /**
   * Returns the move date if set (phase 2), otherwise falls back to the
   * live map's selected date so desk availability is checked against the
   * date the user is currently viewing.
   */
  get activeMoveDate(): Date | undefined {
    return this.moveDate ?? this.args.selectedDate;
  }

  @action
  handleFloorChange(floorId: string): void {
    this.selectedFloorId = floorId;
  }

  @action
  handleDeskSelect(desk: DeskModel | null): void {
    this.selectedDesk = desk;
  }

  @action
  goToNextPhase(): void {
    if (!this.canProceedToDetails) return;
    this.phase = 'move-details';

    // Pre-fill the move date with the date used for Phase 1's desks API call
    // so the picker isn't empty and the conflict can be derived immediately.
    if (this.moveDate == null && this.args.selectedDate != null) {
      this.moveDate = this.args.selectedDate;
    }

    // The selected DeskModel was decorated by the map's loadDesksTask against
    // the same date (activeMoveDate === moveDate), so derive the conflict
    // synchronously from the in-memory record — no re-fetch needed.
    if (this.selectedDesk) {
      this.deriveConflictFromDesk(this.selectedDesk);
    }
  }

  @action
  goToPreviousPhase(): void {
    // Cancel any in-flight conflict check so a late response can't overwrite
    // the synchronous state we'll compute on the next goToNextPhase.
    void this.checkDeskConflictTask.cancelAll();
    this.phase = 'desk-selection';
  }

  @action
  handleClose(): void {
    this.resetState();
    this.args.onClose();
  }

  @action
  updateMoveDate(date: Date | null): void {
    if (date) {
      this.moveDate = date;
      void this.checkDeskConflictTask.perform();
    }
  }

  checkDeskConflictTask = restartableTask(async () => {
    if (!this.selectedDesk || !this.moveDate) {
      this.clearConflict();
      return;
    }

    const dayStart = startOfDay(this.moveDate);
    const desks: DeskModel[] = (
      await this.store.query('desk', {
        filter: {
          id: this.selectedDesk.id,
          'location-id': this.args.locationId,
          'available-from': getUnixTime(dayStart),
          'available-to': getUnixTime(addSeconds(dayStart, SECONDS_IN_A_DAY - 1)),
        },
        fields: { desks: 'accessible' },
      })
    ).slice();

    const desk = desks[0];
    if (!desk) {
      this.clearConflict();
      return;
    }

    this.deriveConflictFromDesk(desk);
  });

  /**
   * Compute conflict flags from a date-scoped DeskModel record. The desk
   * must already have `enabled`, `assignedTo`, and `neighborhood` decorated
   * for the target move date (which is what Phase 1's loadDesksTask produces).
   */
  private deriveConflictFromDesk(desk: DeskModel): void {
    // Blocked: desk is disabled on the move date
    this.conflictBlocked = desk.enabled === false;

    // Restricted: neighborhood has an employee allow-list that excludes the requester
    const neighborhoodId = desk.belongsTo('neighborhood').id();
    this.conflictNeighborhood = null;
    if (neighborhoodId) {
      const neighborhood = this.store.peekRecord('neighborhood', neighborhoodId);
      const allowList = neighborhood?.employeeIds ?? [];
      if (allowList.length > 0 && !allowList.includes(this.args.employeeId)) {
        this.conflictNeighborhood = neighborhood?.name ?? neighborhoodId;
      }
    }

    // Assigned: someone other than the requester holds the desk on the move date
    const assignedTo = desk.assignedTo;
    if (assignedTo && assignedTo !== this.args.employeeEmail) {
      const employee = this.store.peekAll('employee').find((e) => e.email === assignedTo);
      this.conflictAssignee = employee?.name ?? assignedTo;
    } else {
      this.conflictAssignee = null;
    }
  }

  private clearConflict(): void {
    this.conflictAssignee = null;
    this.conflictNeighborhood = null;
    this.conflictBlocked = false;
  }

  @action
  updateMoveRequestReason(event: Event): void {
    this.moveRequestReason = (event.target as HTMLInputElement).value;
  }

  @action
  async submitRequest(): Promise<void> {
    if (!this.canSubmit || !this.selectedDesk || this.isSubmitting) return;

    this.isSubmitting = true;
    try {
      // moveDateTime is in the location's local timezone (passed separately as `timezone`).
      // The backend interprets this datetime using the timezone field.
      const moveDateTime = `${format(this.moveDate!, 'yyyy-MM-dd')}T${this.moveDateStartTime}`;
      await this.ajax.request(urlBuilder.moveRequests.createUrl(), {
        type: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
          employeeId: Number(this.args.employeeId),
          deskId: Number(this.selectedDesk?.id),
          locationId: Number(this.args.locationId),
          moveDateTime,
          timezone: this.args.timezone,
          reason: this.moveRequestReason.trim(),
        }),
      });
      this.resetState();
      this.args.onSubmitted();
    } catch (error) {
      this.logger.error(error instanceof Error ? error : new Error('Failed to create move request'));
      this.flashMessages.showAndHideFlash('error', 'Unable to create move request. Please try again.', {
        showAboveModalOverlay: true,
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  private resetState(): void {
    this.phase = 'desk-selection';
    this.selectedDesk = null;
    this.selectedFloorId = null;
    this.moveDate = null;
    this.moveRequestReason = '';
    this.isSubmitting = false;
    this.clearConflict();
  }
}
